<template>
  <div>
    <!-- Loan Information -->
    <div class="row">
      <FormGroup
        class="col-12"
        label="How much would you like to borrow?"
        :validator="$v.loan.requestamount"
      >
        <TextInput
          v-model.number="model.requestamount"
          v-inputmask="{ type: 'money' }"
          required
          placeholder="$ 0.00"
        />
      </FormGroup>
    </div>

    <div class="row">
      <FormGroup
        class="col-12"
        label="What state do you live in?"
        :validator="$v.loan.state"
      >
        <select
          v-model="model.state"
          required
        >
          <option value>
            - Select -
          </option>
          <option
            v-for="item in states"
            :key="item.id"
            :value="item.id"
          >
            {{ item.id }}
          </option>
        </select>
      </FormGroup>
    </div>

    <div class="row">
      <FormGroup
        class="col-12"
        label="What is the loan for?"
        :validator="$v.loan.loan_purpose"
      >
        <select
          v-model="model.loan_purpose"
          required
        >
          <option value>
            - Select -
          </option>
          <option
            v-for="item in purposes"
            :key="item.id"
            :value="item.id"
          >
            {{ item.id }}
          </option>
        </select>
      </FormGroup>
    </div>
    <!-- ./Loan Information -->

    <!-- Personal Information -->
    <h1 class="mt-l">
      Personal Information
    </h1>

    <div class="row">
      <div class="col-12 headerUnderline">
        <img src="./assets/images/Underline.png">
      </div>
    </div>

    <div class="row">
      <FormGroup
        class="col-6"
        label="First Name"
        :validator="$v.loan.firstname"
      >
        <TextInput
          v-model="model.firstname"
          :readonly="status.isReturningCustomer"
          required
        />
      </FormGroup>

      <FormGroup
        class="col-6"
        label="Last Name"
        :validator="$v.loan.lastname"
      >
        <TextInput
          v-model="model.lastname"
          :readonly="status.isReturningCustomer"
          required
        />
      </FormGroup>
    </div>

    <div class="row">
      <FormGroup
        class="col-6"
        label="Email Address"
        :validator="$v.loan.email"
      >
        <TextInput
          v-model="model.email"
          class="fs-unmask"
          type="email"
          :readonly="status.isReturningCustomer"
          required
        />
      </FormGroup>

      <FormGroup
        class="col-6"
        label="Phone"
        :validator="$v.loan.homephone"
      >
        <TextInput
          v-model="model.homephone"
          v-inputmask="{ type: 'us-phone' }"
          :readonly="status.isReturningCustomer"
          required
        />
      </FormGroup>
    </div>

    <div class="row">
      <FormGroup
        class="col-12"
        label="Physical Address"
        :validator="$v.loan.address"
      >
        <TextInput
          v-model="model.address"
          required
        />
      </FormGroup>
    </div>

    <div class="row">
      <FormGroup
        class="col-12"
        label="Apartment or Suite Number"
      >
        <TextInput
          v-model="model.address1"
          required
        />
      </FormGroup>
    </div>

    <div class="row">
      <FormGroup
        class="col-6"
        label="City"
        :validator="$v.loan.city"
      >
        <TextInput
          v-model="model.city"
          required
        />
      </FormGroup>

      <FormGroup
        class="col-6"
        label="Zip Code"
        :validator="$v.loan.zip"
      >
        <TextInput
          v-model="model.zip"
          v-inputmask="{ type: 'zip' }"
          required
        />
      </FormGroup>
    </div>
    <!-- ./ Personal Information -->

    <div class="row mt-m">
      <FormGroup
        class="col-12"
        :validator="$v.recaptcha"
      >
        <Recaptcha
          @verifyRecaptcha="onVerifyRecaptcha"
          @recaptchaExpired="onRecaptchaExpired"
        />
        <template #error>
          <i v-if="!$v.recaptcha.checked">
            Required
          </i>
        </template>
      </FormGroup>
    </div>

    <div class="row mt-m mb-s">
      <div class="col-12 col-12-medium">
        <ul class="actions fit">
          <li>
            <button
              class="button primary fit icon fa-lock"
              @click="handleNext()"
            >
              CLICK TO CONTINUE
            </button>
          </li>
        </ul>
      </div>
    </div>

    <div class="row">
      <div class="col-12 col-12-small">
        By clicking this button, you agree to our
        <a
          :href="settings.link_terms_of_use_href"
          target="_blank"
        >{{ settings.link_sms_terms_label }}</a>,
        <a
          :href="settings.link_esign_consent_href"
          target="_blank"
        >{{ settings.link_esign_consent_label }}</a>,
        and
        <a
          :href="settings.link_privacy_policy_href"
          target="_blank"
        >{{ settings.link_privacy_policy_label }}</a>.
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import Recaptcha from "@/components/Recaptcha.vue";
import jsonStates from "@/config/states.json";
import { loanPurposes } from "@/config/walkupFields";
import validations from "../validations";

export default {
  name: "StepOne",
  components: { Recaptcha },

  data() {
    return {
      states: jsonStates,
      purposes: loanPurposes,
      model: {},
      stepFields: [
        "requestamount",
        "state",
        "loan_purpose",
        "firstname",
        "lastname",
        "email",
        "homephone",
        "address",
        "city",
        "zip"
      ],
      recaptcha: null,
      baseApiUrl: process.env.VUE_APP_UNDERWRITING_URL,
    };
  },

  computed: {
    ...mapState("settings", ["settings"]),
    ...mapState("underwriting", ["status", "loan"]),
  },

  watch: {
    "model.state": {
      deep: true,
      handler(value) {
        if (value) {
          this.checkLendingStates(value);
        }
      }
    }
  },

  mounted() {
    this.model = this.loan;
  },

  validations() {
    return {
      loan: validations,
      recaptcha: {
        checked: (v) => v === true
      },
    };
  },

  methods: {
    ...mapActions("underwriting", ["updateLoan"]),
    ...mapActions("settings", ["checkLendingStates"]),

    handleNext() {
      let isValid = true;
      this.updateLoan({ ...this.model });
      this.stepFields.forEach((field) => {
        if (this.$v.loan[field]) {
          this.$v.loan[field].$touch();
          isValid = isValid && !this.$v.loan[field].$anyError;
        }

        this.$v.recaptcha.$touch();
        isValid = isValid && !this.$v.recaptcha.$anyError;
      });

      if (isValid) {
        this.$router.push("/step-2");
      } else {
        this.$nextTick(() => {
          this.scroll(true);
        });
      }
    },

    scroll(toError = false) {
      let top = 0;

      if (toError) {
        const domRect = document
          .querySelector(".with-errors")
          .getBoundingClientRect();
        top = domRect.top + document.documentElement.scrollTop - 230;
      }

      window.scrollTo({
        top,
        behavior: "smooth",
      });
    },

    async onVerifyRecaptcha(token) {
      // backend returns boolean after checking this token against the secret key
      if (token) {
        try {
          const response = await this.$axios.get(`${this.baseApiUrl}customer/recaptcha-verify/${token}`);

          if (response.status === 200) {
            this.recaptcha = true;
          }
        } catch (e) {
          this.recaptcha = false;
        }
      }
    },

    onRecaptchaExpired() {
      this.recaptcha = false;
    },
  }

};
</script>
